import LandingLayout from '../../components/layout/LandingLayout';

export default function HIPAANotice() {
  return (
    <LandingLayout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-3xl font-extrabold text-neutral-900 mb-2">HIPAA Notice of Privacy Practices</h1>
        <p className="text-sm text-neutral-500 mb-10">Effective date: April 24, 2026</p>

        <div className="bg-primary-50 border border-primary-100 rounded-xl px-6 py-4 mb-10 text-sm text-primary-800 font-medium">
          THIS NOTICE DESCRIBES HOW HEALTH INFORMATION ABOUT YOU MAY BE USED AND DISCLOSED
          AND HOW YOU CAN GET ACCESS TO THIS INFORMATION. PLEASE REVIEW IT CAREFULLY.
        </div>

        <div className="prose prose-neutral max-w-none space-y-8 text-neutral-700 leading-relaxed">

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">Who this notice applies to</h2>
            <p>
              This Notice of Privacy Practices describes how Sage Mental Inc. (&quot;Sage&quot;) may use and
              disclose your Protected Health Information (PHI) in our role as a business associate to
              healthcare providers who use the Sage platform. It also describes your rights with respect
              to that information.
            </p>
            <p className="mt-3">
              Your healthcare provider (the covered entity) has their own separate Notice of Privacy
              Practices that governs how they use and share your health information. Please ask your
              provider for a copy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">Our privacy commitment</h2>
            <p>
              We are required by law to maintain the privacy of your PHI, to provide you with this
              notice of our legal duties and privacy practices, and to notify you following a breach of
              unsecured PHI. We are required to follow the terms of this notice currently in effect.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">How we may use and disclose your PHI</h2>

            <h3 className="text-base font-semibold text-neutral-800 mt-4 mb-2">Treatment</h3>
            <p>
              We may use and disclose PHI to facilitate the health care your provider delivers — for
              example, transmitting appointment records, clinical notes, or lab results among members
              of your care team as directed by your provider.
            </p>

            <h3 className="text-base font-semibold text-neutral-800 mt-4 mb-2">Payment</h3>
            <p>
              We may use and disclose PHI to support billing and collection activities, including
              submitting claims to insurance companies and processing payments, as directed by your
              provider.
            </p>

            <h3 className="text-base font-semibold text-neutral-800 mt-4 mb-2">Health care operations</h3>
            <p>
              We may use and disclose PHI for health care operations activities of your provider,
              including quality assessment, training, and practice management functions.
            </p>

            <h3 className="text-base font-semibold text-neutral-800 mt-4 mb-2">As required by law</h3>
            <p>
              We will disclose PHI when required to do so by federal, state, or local law — for example,
              in response to a court order, public health reporting requirements, or a government audit.
            </p>

            <h3 className="text-base font-semibold text-neutral-800 mt-4 mb-2">Business associates</h3>
            <p>
              We may share PHI with our subprocessors (cloud infrastructure, email delivery, payment
              processors) who are bound by written Business Associate Agreements and required to
              safeguard your information.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">Uses and disclosures requiring authorization</h2>
            <p className="mb-3">
              We will not use or disclose your PHI for the following purposes without your written
              authorization:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Marketing purposes</li>
              <li>Sale of PHI</li>
              <li>Most uses of psychotherapy notes</li>
              <li>Any other use or disclosure not described in this notice</li>
            </ul>
            <p className="mt-3">
              You may revoke an authorization at any time in writing, except to the extent that we have
              already taken action in reliance on it.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">Your rights regarding your PHI</h2>

            <h3 className="text-base font-semibold text-neutral-800 mt-4 mb-2">Right to access</h3>
            <p>
              You have the right to inspect and obtain a copy of your PHI in a designated record set.
              To exercise this right, contact your healthcare provider.
            </p>

            <h3 className="text-base font-semibold text-neutral-800 mt-4 mb-2">Right to amend</h3>
            <p>
              You have the right to request that your provider amend PHI that you believe is incorrect
              or incomplete.
            </p>

            <h3 className="text-base font-semibold text-neutral-800 mt-4 mb-2">Right to an accounting of disclosures</h3>
            <p>
              You may request a list of certain disclosures we have made of your PHI in the six years
              prior to your request.
            </p>

            <h3 className="text-base font-semibold text-neutral-800 mt-4 mb-2">Right to request restrictions</h3>
            <p>
              You have the right to request restrictions on certain uses and disclosures of your PHI,
              though we are not required to agree to all restrictions.
            </p>

            <h3 className="text-base font-semibold text-neutral-800 mt-4 mb-2">Right to receive confidential communications</h3>
            <p>
              You may request that we communicate with you about your health information in a specific
              way or at a specific location.
            </p>

            <h3 className="text-base font-semibold text-neutral-800 mt-4 mb-2">Right to a copy of this notice</h3>
            <p>You have the right to a paper copy of this notice upon request.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">Breach notification</h2>
            <p>
              We will notify affected individuals and, as required, the Secretary of the U.S. Department
              of Health and Human Services, in the event of a breach of unsecured PHI, consistent with
              45 C.F.R. §§ 164.400–414.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">Changes to this notice</h2>
            <p>
              We reserve the right to change this notice and to apply the revised notice to PHI we
              already hold. We will post the current notice at sagemental.net/hipaa-notice and make
              it available upon request.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">Complaints</h2>
            <p className="mb-3">
              If you believe your privacy rights have been violated, you may file a complaint with us
              or with the U.S. Department of Health and Human Services, Office for Civil Rights.
              You will not be retaliated against for filing a complaint.
            </p>
            <p>
              To file a complaint with us:<br />
              Sage Mental Inc. — Privacy Officer<br />
              <a href="mailto:privacy@sagemental.net" className="text-primary-600 hover:underline">privacy@sagemental.net</a>
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">Contact</h2>
            <p>
              For questions about this notice or our privacy practices:<br />
              Sage Mental Inc.<br />
              <a href="mailto:privacy@sagemental.net" className="text-primary-600 hover:underline">privacy@sagemental.net</a>
            </p>
          </section>

        </div>
      </div>
    </LandingLayout>
  );
}
